import { cn } from "@/lib/utils";
import React, { FocusEvent, useState } from "react";

interface SelectDropDownProps {
  label: string;
  required?: boolean;
  optionsList: {
    id: number | string;
    label: string;
    icon?: any;
    subTitles?: { id: string; label: string }[];
  }[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  value: number | string;
  errorMessage?: string;
  placeholder?: string;
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  optgroup?: boolean;
}

const SelectDropDown = (props: SelectDropDownProps) => {
  const {
    label,
    required,
    optionsList,
    onChange,
    value,
    errorMessage,
    placeholder,
    onFocus,
    onBlur,
    optgroup = true, // Default to true when not passed
  } = props;

  const [active, setActive] = useState(false);

  const handleFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
    setActive(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
    setActive(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <div>
      <div
        className={cn(
          "flex justify-center items-center relative h-[56px] rounded-[8px]",
          errorMessage
            ? "border border-border-token-error-subtle bg-bg-error-tertiary-default"  
            : active
              ? "border border-border-token-neutral-subtle bg-bg-neutral-secondary-default"
              : "border border-border-token-neutral-subtle bg-bg-neutral-secondary-default",
        )}
      >
        <select
          className={[
            "appearance-none outline-none w-full rounded label-m-medium transition-all duration-200 ease-in-out px-4 relative bg-bg-neutral-secondary-default text-fg-neutral-strong",
            active ? "pt-3" : "pt-3",
          ].join("")}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={placeholder || label}
        >
          {optionsList?.map((item) => {
            // If the item has subTitles, render them based on optgroup prop
            if (item.subTitles && item.subTitles.length > 0) {
              if (optgroup) {
                // Render with optgroup when optgroup is true
                return (
                  <optgroup key={item.id} label={item.label}>
                    {item.subTitles.map((subItem) => (
                      <option
                        key={`${item.id}.${subItem.id}`}
                        value={`${item.id}.${subItem.id}`}
                        className="bg-bg-neutral-secondary-default text-fg-neutral-strong"
                      >
                        {subItem.label}
                      </option>
                    ))}
                  </optgroup>
                );
              } else {
                // Render as normal options with indentation when optgroup is false
                return [
                  // First show the parent item as an option
                  <option
                    key={`${item.id}.0`}
                    value={`${item.id}.0`}
                    className="bg-bg-neutral-secondary-default text-fg-neutral-strong"
                  >
                    {item.label}
                  </option>,
                  // Then show subitems with indentation
                  ...item.subTitles.map((subItem) => (
                    <option
                      key={`${item.id}.${subItem.id}`}
                      value={`${item.id}.${subItem.id}`}
                      className="bg-bg-neutral-secondary-default text-fg-neutral-strong"
                    >
                      &nbsp;&nbsp;&nbsp;&nbsp;{subItem.label}
                    </option>
                  )),
                ];
              }
            }
            // If no subTitles, render as a regular option
            return (
              <option
                key={item.id}
                value={item.id}
                className="bg-bg-neutral-secondary-default text-fg-neutral-strong"
              >
                {item.label}
              </option>
            );
          })}
        </select>

        {/* Custom arrow */}
        <div className="absolute right-4 pointer-events-none" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-fg-neutral-subtle"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        <label
          className={[
            "absolute left-2 flex items-center px-2 transition-all duration-200 ease-in-out body-s-regular top-[2px] text-fg-neutral-strong",
            errorMessage
              ? ""
              : active
                ? "!text-fg-brand-subtle"
                : "text-fg-neutral-subtle",
          ].join("")}
          htmlFor={label}
        >
          {placeholder || label}
          {" "}
          {required && <span className="text-fg-brand-subtle">*</span>}
        </label>
      </div>
    </div>
  );
};

export default SelectDropDown;
